/**
 * reverse-webcodecs.js — hardware reverse / boomerang video engine.
 *
 *   File → MP4Box demux → VideoDecoder(hw) → per-GOP frame cache →
 *     frames served in REVERSE order → CanvasVideoExport (VideoEncoder hw +
 *     mp4/webm-muxer + OPFS + AAC/Opus audio) → Blob
 *
 * WHY this exists — /reverse-video used to reverse via FFmpeg-WASM `-vf reverse`,
 * which buffers EVERY decoded frame in the 2 GB WASM heap (random-access filter)
 * → OOM. The workaround (FFmpegProcessor.reverseChunked) splits into 4 s chunks,
 * reverses each and concats in reverse order — correct, but software x264 in WASM
 * runs ~3-5 fps on mobile and the `-ss/-to` chunk seams can drop/dup a frame.
 *
 * This module reuses the SAME hardware pipeline video-merge / enhance-video use:
 *   - the DECODE side (mp4box + VideoDecoder) is owned here, because reverse
 *     needs random access to frames in reverse presentation order;
 *   - the ENCODE + MUX + AUDIO + OPFS side is delegated to CanvasVideoExport,
 *     whose drawFrame(ctx,i) callback is AWAITED (canvas-video-export.js:523),
 *     so we decode the needed GOP on demand and hand back the reversed frame.
 *
 * Memory is bounded to a WINDOW of ImageBitmaps (frameCacheBudget bytes),
 * independent of GOP length or resolution. A GOP that fits the window is
 * decoded once and cached whole (the fast path — normal videos never
 * re-decode). A GOP larger than the window (4K, or very sparse keyframes) is
 * served by a SLIDING window: decode from the keyframe up to the requested
 * frame, keep that slice at FULL resolution, re-decode from the keyframe as the
 * window slides. This keeps huge-GOP / 4K sources on the fast hardware path at
 * full quality instead of downscaling or dropping to FFmpeg. Only the
 * pathological tail — a single enormous GOP, or frames so large even a minimal
 * window won't fit — falls back (bounded by the MAX_REDECODE_WINDOWS gate).
 *
 * Falls back (caller catches the thrown reason) when: no WebCodecs, source
 * codec the decoder rejects, container mp4box can't demux (caller remuxes -c
 * copy first, or falls back), a single GOP so large even windowing it would
 * re-decode too many times, GIF output (kept on the FFmpeg palette path), or
 * any runtime error mid-pipeline.
 *
 * API — window.ReverseWebCodecs:
 *
 *   await isSupported({ file, format }) →
 *      { ok:true, decoderHardware, encoderHardware, codec, width, height, fps,
 *        frames, maxGopFrames, durationSec }
 *    | { ok:false, reason }
 *
 *   await reverse(file, {
 *      mode:  'reverse' | 'boomerang',
 *      audio: 'keep' | 'reverse' | 'mute',
 *      speed: 0.5 | 1 | 1.5 | 2,
 *      format:'mp4' | 'webm',
 *      crf:   18..35,
 *      trim:  { start, end } | null,      // seconds
 *   }, {
 *      onProgress: frac => void,
 *      onStage:    name => void,          // 'demux' | 'audio' | 'encode'
 *      onInfo:     info => void,          // { decoderHardware, frames, ... }
 *      signal:     AbortSignal,
 *   }) → Blob
 *
 * Dependencies: window.MP4Box (/vendor/mp4box), window.CanvasVideoExport
 * (canvas-video-export.js — pulls in mp4-muxer / webm-muxer), and optionally
 * window.WebCodecsAudioDecode for robust mov/aac audio decode.
 */
(function () {
'use strict';
if (window.ReverseWebCodecs) return;

/* ───────────────────────── budgets / tuning ───────────────────────── */

// Peak bytes of ImageBitmap frame-cache we allow (≈ one GOP held at once).
// ImageBitmap of a decoded frame ≈ w*h*4 (RGBA). Desktop gets a generous
// budget; mobile is tighter because a wedged tab OOM-kills the whole page.
function frameCacheBudget() {
    // Test seam: force a tiny budget to exercise the sliding-window path on small
    // fixtures (tests/playwright/reverse-video-windowing-scratch). Guarded — only
    // fires when a test explicitly sets it, so it has no effect in production.
    var forced = (typeof window !== 'undefined') && window.__RWC_FORCE_BUDGET_BYTES;
    if (forced && forced > 0) return forced;
    var mobile = /Android|iPhone|iPad|iPod/i.test((typeof navigator !== 'undefined' && navigator.userAgent) || '');
    // Scale by device RAM (navigator.deviceMemory is capped at 8 for privacy). A
    // high-memory desktop can hold a long-GOP 1080p clip's frames (~2 GB) that a
    // 4 GB machine can't — so give it the headroom instead of dropping to FFmpeg,
    // while a low-memory / mobile device stays conservative. 4 GB → ~1400 MB
    // (unchanged), 8 GB → ~2400 MB, 2 GB → ~900 MB.
    var dmem = (typeof navigator !== 'undefined' && navigator.deviceMemory) || (mobile ? 3 : 4);
    var mb = mobile ? Math.min(dmem * 130, 1000) : Math.min(dmem * 340, 2400);
    mb = Math.max(mb, mobile ? 450 : 900);
    return mb * 1024 * 1024;
}

// Sliding-window gate. A window smaller than MIN_WINDOW_FRAMES gives too little
// runway to be worth re-decoding (frames so large — 8K — that even a handful
// won't fit the budget) → FFmpeg. A GOP that needs more than MAX_REDECODE_WINDOWS
// windows means every frame is re-decoded that many times: past ~8× the HW
// re-decode stops beating FFmpeg's single streamed pass, so that pathological
// tail (a single enormous GOP) goes to FFmpeg — at FULL quality, never a downscale.
var MIN_WINDOW_FRAMES = 4;
var MAX_REDECODE_WINDOWS = 8;

/* ⛔ MEASURED AND REJECTED — holding frames as CPU pixels instead of GPU
   ImageBitmaps. The reasoning was that I420 is w*h*1.5 against RGBA's w*h*4, so
   a held GOP would cost 2.7x less. Built it (VideoFrame.copyTo into a
   Uint8Array, rebuilt into a VideoFrame only to draw) and measured it on the
   real page, 1080p / 240 frames / one GOP, best of two runs:

       store    wall      peak RSS delta   frame-exact
       bitmap    5.2 s        114 MB           yes
       planar   15.5 s        969 MB           yes      (3x slower, 8x the RSS)

   Both numbers go the wrong way because ImageBitmaps are not in system memory
   at all — they are GPU allocations, and the process tree barely sees them,
   while CPU planes are plain RSS plus a readback per frame and an upload per
   draw. So the answer to "this device cannot hold the window" is to hold FEWER
   frames (the shrink ladder in decodeWindow), not to change what a held frame
   is. Do not re-try this without a new measurement.

   ⚠️ And the corollary for reading the numbers above: on desktop the held GOP
   is nearly free in RSS, so RSS is the wrong instrument for it. On a phone the
   GPU shares system memory and is far smaller, which is why the device in
   #6865 ran out and this machine never does. */

// Ceiling on createImageBitmap copies in flight at once — the decoder's OUTPUT
// side, which had no bound. See the drain in decodeWindowOnce.
var MAX_INFLIGHT_COPIES = 24;

// Hard ceiling on attempts for ONE window — the ladder mixes config retries and
// window shrinks, and neither may turn into an open-ended loop.
var MAX_WINDOW_ATTEMPTS = 7;

// DECODER hints — HARDWARE FIRST ON EVERY PLATFORM.
//
// Here stood the ENCODER's Android inversion ('prefer-software' first),
// copied over as "a cheap safety margin". It was a guess, and the fleet
// priced it: 289 of 383 mobile software-decode runs over 30 days died with
// «Unsupported configuration…» minutes into the export, against 2 of 357 on
// hardware. Chromium on Android has no software H.264 decoder to prefer —
// only a probe that says it does. Software is the risky lane here, not the
// safe one, so it stays as the FALLBACK, which is what it is.
//
// Only reachable when decoder-verify.js failed to load; the verified pick
// (window.DecoderVerify) owns the ladder otherwise and carries the numbers.
var DECODE_HINTS = ['prefer-hardware', 'prefer-software', null];

/* ───────────────────────── loaders ───────────────────────── */

function loadMp4Box() {
    if (window.MP4Box) return Promise.resolve();
    if (window.__mp4boxLoadingPromise) return window.__mp4boxLoadingPromise;
    window.__mp4boxLoadingPromise = new Promise(function (resolve, reject) {
        var s = document.createElement('script');
        s.src = '/vendor/mp4box/mp4box.all.min.js';
        s.onload = function () { resolve(); };
        s.onerror = function () {
            var sources = ['/cdn/jsdelivr/npm/mp4box@0.5.4/dist/mp4box.all.min.js',
                           'https://cdn.jsdelivr.net/npm/mp4box@0.5.4/dist/mp4box.all.min.js'];
            (function next(i) {
                if (i >= sources.length) { reject(new Error('mp4box-load-failed')); return; }
                var s2 = document.createElement('script');
                s2.src = sources[i]; s2.onload = function () { resolve(); };
                s2.onerror = function () { next(i + 1); };
                document.head.appendChild(s2);
            })(0);
        };
        document.head.appendChild(s);
    });
    return window.__mp4boxLoadingPromise;
}

function isMp4Family(file) {
    var n = ((file && file.name) || '').toLowerCase(), t = ((file && file.type) || '').toLowerCase();
    return /\.(mp4|m4v|mov|3gp|3g2)$/i.test(n) || /^video\/(mp4|quicktime|3gpp|3gpp2)$/i.test(t);
}

/* Decoder config record for ANY codec: mp4box already parsed the config box, so
   writing it back and stripping its header IS the record VideoDecoder wants.
   ⚠️ Until 2026-08-08 this hand-packed avcC and produced a record that was wrong
   on 14/14 files of the real fixture corpus — one uninitialised trailing byte
   (allocated `7+2+…`, wrote 8 header bytes) and, on High profiles, the loss of
   the trailing fields ISO/IEC 14496-15 §5.3.3.1.2 requires. Browsers re-derive
   those from the SPS, which is why it never surfaced. It also meant HEVC/VP9/AV1
   got NO description at all and always fell to the slow probe.
   Canonical version: VideoMergeWebCodecs.buildDecoderDescription (it additionally
   slices the record straight from the source bytes when the caller holds the whole
   file; this module streams mp4box in chunks, so re-serialising is the right tier). */
function buildConfigDescription(box) {
    if (!box) return null;
    try {
        var DS = (window.MP4Box && window.MP4Box.DataStream) || window.DataStream;
        if (!DS || typeof box.write !== 'function') return null;
        var stream = new DS(undefined, 0, DS.BIG_ENDIAN);
        box.write(stream);
        // hdr_size, not a hardcoded 8 — vpcC is a FullBox (8 + 4 version/flags).
        var hdr = (box.hdr_size != null) ? box.hdr_size : 8;
        var all = new Uint8Array(stream.buffer);
        return all.length > hdr ? all.subarray(hdr) : null;
    } catch (_) { return null; }
}

/* Read the display rotation (0/90/180/270) from the tkhd matrix so portrait
   phone clips (landscape pixels + a rotation flag) reverse in the orientation
   the viewer expects. Ported verbatim from the proven webcodecs-pipeline.js
   extraction: mp4 stores a 3×3 matrix in 16.16 fixed-point; the top-left 2×2
   (a=m[0], b=m[1]) encodes the rotation. mp4box exposes it on
   getTrackById(id).tkhd.matrix — NOT on the track-info object. */
function trackRotation(mp4, trackId) {
    /* Ридер живёт в одном месте — public/js/video-orientation.js. Здесь стояла
       его копия («ported verbatim»), и копий таких было четыре: разъехаться им
       ничто не мешало, а расхождение видно только на съёмке с телефона. */
    var vo = window.VideoOrientation;
    if (vo) return vo.fromTkhd(mp4, trackId);
    return 0;
}

/* ───────────────────────── demux ─────────────────────────
   Returns, for the primary video track:
     { codec, description, codedW, codedH, rotation, durationSec, fps,
       samples: [{ type:'key'|'delta', cts(us), dur(us), data }]  (decode order),
       pres:    [ sampleIndexSortedByCts ]  (presentation order),
       gops:    [{ sStart, sEnd, pStart, pEnd, frames }]  (contiguous ranges),
       maxGopFrames }
*/
function demux(file, signal) {
    return new Promise(function (resolve, reject) {
        loadMp4Box().then(run, reject);
        function run() {
            var mp4 = MP4Box.createFile();
            var samples = [], info = null, vt = null, desc = null, done = false;
            function rej(e) { if (!done) { done = true; reject(e instanceof Error ? e : new Error(String(e))); } }
            if (signal) signal.addEventListener('abort', function () { rej(new DOMException('Aborted', 'AbortError')); }, { once: true });

            mp4.onError = function (e) { rej(new Error('mp4box:' + e)); };
            mp4.onReady = function (inf) {
                info = inf;
                var vtr = inf.tracks.filter(function (t) { return t.video; })[0];
                if (!vtr) { rej(new Error('no-video-track')); return; }
                vt = vtr;
                try {
                    var e0 = mp4.getTrackById(vt.id).mdia.minf.stbl.stsd.entries[0];
                    desc = buildConfigDescription(e0.avcC || e0.hvcC || e0.vpcC || e0.av1C);
                } catch (e) { desc = null; }
                mp4.setExtractionOptions(vt.id, null, { nbSamples: 1000000 });
                mp4.start();
            };
            mp4.onSamples = function (id, user, s) {
                for (var i = 0; i < s.length; i++) {
                    var x = s[i];
                    samples.push({
                        type: x.is_sync ? 'key' : 'delta',
                        cts: Math.round(x.cts * 1e6 / x.timescale),
                        dur: Math.round(x.duration * 1e6 / x.timescale),
                        data: x.data
                    });
                }
                try { mp4.releaseUsedSamples(id, s[s.length - 1].number); } catch (_) {}
            };

            // Stream the file into mp4box (bounded reads; no whole-file heap copy).
            var reader = file.stream().getReader(), offset = 0;
            (function pump() {
                if (done) return;
                reader.read().then(function (r) {
                    if (r.done) {
                        try { mp4.flush(); } catch (_) {}
                        return finalize();
                    }
                    var chunk = r.value;
                    var buf = (chunk.byteOffset === 0 && chunk.byteLength === chunk.buffer.byteLength)
                        ? chunk.buffer
                        : chunk.buffer.slice(chunk.byteOffset, chunk.byteOffset + chunk.byteLength);
                    buf.fileStart = offset; offset += chunk.byteLength;
                    try { mp4.appendBuffer(buf); } catch (e) { return rej(e); }
                    pump();
                }).catch(rej);
            })();

            function finalize() {
                if (done) return;
                if (!vt) return rej(new Error('no-video-track'));
                if (!samples.length) return rej(new Error('no-samples'));
                if (vt.codec && /^avc/i.test(vt.codec) && !desc) return rej(new Error('avcc-missing'));
                done = true;

                var codedW = vt.video.width, codedH = vt.video.height;
                var rotation = trackRotation(mp4, vt.id);
                var durationSec = vt.duration / vt.timescale;

                // Presentation order = samples sorted by cts (handles B-frame reorder).
                var pres = samples.map(function (_, i) { return i; })
                    .sort(function (a, b) { return samples[a].cts - samples[b].cts; });
                var presRank = new Array(samples.length);
                for (var k = 0; k < pres.length; k++) presRank[pres[k]] = k;

                // GOPs = decode-order runs starting at each keyframe. Their
                // presentation ranges are contiguous for standard encoders; we
                // compute [pStart,pEnd] as the min/max presRank of the GOP's
                // samples so the provider can map a presIdx → its GOP.
                var gops = [], cur = null, maxGopFrames = 0;
                for (var i = 0; i < samples.length; i++) {
                    if (samples[i].type === 'key' || !cur) {
                        if (cur) { gops.push(cur); maxGopFrames = Math.max(maxGopFrames, cur.frames); }
                        cur = { sStart: i, sEnd: i + 1, pStart: presRank[i], pEnd: presRank[i], frames: 1 };
                    } else {
                        cur.sEnd = i + 1; cur.frames++;
                        if (presRank[i] < cur.pStart) cur.pStart = presRank[i];
                        if (presRank[i] > cur.pEnd) cur.pEnd = presRank[i];
                    }
                }
                if (cur) { gops.push(cur); maxGopFrames = Math.max(maxGopFrames, cur.frames); }

                var fps = durationSec > 0 ? Math.round(samples.length / durationSec) : 30;
                if (!isFinite(fps) || fps < 1) fps = 30;

                // hasAudio: bias toward "there might be audio" so we NEVER silently
                // drop a real track — only mute when we're confident there's none.
                // mp4box mislabels PCM (sowt/twos/lpcm) tracks as type:"metadata"
                // with audio:false, so `t.audio` alone false-negatives and would
                // mute real audio. A non-video track counts as audio UNLESS its
                // codec is a KNOWN non-audio kind (timecode/subtitle/data).
                var hasAudio = false;
                try {
                    var KNOWN_NONAUDIO = /^(tmcd|gpmd|mebx|tx3g|wvtt|stpp|c608|c708|url |sbtl|text|subp|fdsc|pcst|mp7t|rtp )/i;
                    hasAudio = !!(info && info.tracks && info.tracks.some(function (t) {
                        if (t.video) return false;
                        if (t.audio || t.type === 'audio') return true;
                        return !KNOWN_NONAUDIO.test(String(t.codec || ''));   // unknown non-video → assume maybe-audio
                    }));
                } catch (_) { hasAudio = true; }   // on any doubt, attempt decode (never silently mute)

                resolve({
                    codec: vt.codec, description: desc,
                    codedW: codedW, codedH: codedH, rotation: rotation,
                    durationSec: durationSec, fps: fps, hasAudio: hasAudio,
                    samples: samples, pres: pres, presRank: presRank,
                    gops: gops, maxGopFrames: maxGopFrames
                });
            }
        }
    });
}

/* ───────────────────────── decoder config probe ─────────────────────────
   Delegated to the shared primitive (decoder-verify.js), which earns the
   verdict by DECODING one of this file's real keyframes instead of asking
   isConfigSupported. Asking is what used to happen here, and on Android it
   answered `supported:true` for a software H.264 decoder Chromium does not
   ship: configure() accepted the config too, and the run died minutes later
   with «Unsupported configuration. Check isConfigSupported() prior to calling
   configure()» — 289 of 383 mobile software-decode runs over 30 days, each one
   dropped onto the ~20-minute FFmpeg-WASM grind. Full measurement and the
   history (this same root was fixed once already, in webcodecs-pipeline.js on
   2026-07-10) live in the primitive's header.

   `uncertain` (no keyframe / every hint timed out) keeps the old optimism: we
   try the candidate anyway, because a wedge we cannot attribute must not cost
   the fast lane. Only a clean refusal from every hint is a no. */
async function pickDecoderConfig(codec, description, keyframe, dims) {
    if (typeof VideoDecoder === 'undefined') return null;
    if (!window.DecoderVerify) {
        /* Primitive failed to load (flaky network). Ask, as before — degraded
           but never worse than the code this replaced. */
        for (var h = 0; h < DECODE_HINTS.length; h++) {
            var cfg = { codec: codec };
            if (description) cfg.description = description;
            if (DECODE_HINTS[h]) cfg.hardwareAcceleration = DECODE_HINTS[h];
            try {
                var r = await VideoDecoder.isConfigSupported(cfg);
                if (r && r.supported) {
                    return { config: r.config || cfg, hardware: DECODE_HINTS[h] === 'prefer-hardware', verified: false };
                }
            } catch (_) {}
        }
        return null;
    }
    var v = await window.DecoderVerify.pick({
        codec: codec, description: description,
        codedWidth: (dims && dims.w) || undefined,
        codedHeight: (dims && dims.h) || undefined,
        keyframe: keyframe, slug: 'reverse-video'
    });
    if (!v.ok && !v.uncertain) return null;          // verified refusal
    return { config: v.config, hardware: !!v.hardware, verified: !!v.ok };
}

/* First keyframe of the demuxed stream — the probe's raw material. */
function firstKeyframe(dm) {
    if (!dm || !dm.samples) return null;
    for (var i = 0; i < dm.samples.length; i++) {
        if (dm.samples[i].type === 'key') {
            return { data: dm.samples[i].data, timestamp: dm.samples[i].cts, duration: dm.samples[i].dur };
        }
    }
    return null;
}

/* ───────────────────── bounded-window frame provider ─────────────────────
   Serves getBitmap(presIdx) with resident memory bounded to `windowFrames`
   decoded frames, INDEPENDENT of GOP length or resolution.

   • GOP ≤ window  → decoded once, cached whole (fast path — normal videos,
     zero re-decode; identical to the old single-GOP cache).
   • GOP > window  → SLIDING window: on a miss, decode from the GOP's keyframe
     up to the requested frame (plus direction runway), keep only that slice at
     FULL resolution, close every other frame the instant it decodes. Re-decode
     from the keyframe as the window slides down (reverse) or up (forward).

   This is the clean alternative to downscaling 4K / long-GOP sources to fit the
   cache: full quality is preserved and the re-decode cost is bounded by the
   isSupported gate (≤ MAX_REDECODE_WINDOWS windows per GOP). `maxWindows`
   resident windows — 1 for reverse/boomerang/segment; loop reads a head frame
   and a tail frame (two different GOPs) per output frame, so it needs 2. */
function makeFrameProvider(dm, decoderCfg, signal, maxWindows, windowFrames) {
    maxWindows = Math.max(1, maxWindows || 1);
    windowFrames = Math.max(2, windowFrames || dm.maxGopFrames);
    var cache = [];                          // [{ g, lo, hi, bitmaps }] — MRU last
    var gopLastPres = Object.create(null);   // g → last presIdx read (slide direction)

    function gopForPres(presIdx) {
        for (var g = 0; g < dm.gops.length; g++) {
            if (presIdx >= dm.gops[g].pStart && presIdx <= dm.gops[g].pEnd) return g;
        }
        return -1;
    }

    // Decode GOP `g` from its keyframe, keeping only presentation frames in
    // [lo,hi]; every other decoded frame is closed the instant it arrives, so
    // resident memory is (hi-lo+1) bitmaps. A fresh decoder per window starts
    // cleanly on the keyframe (config cost is µs vs the decode). `feedEnd` stops
    // the decode at the last sample the window needs — a window near the GOP
    // start never decodes the whole GOP.
    /* One window, one decoder, ONE retry ladder.
       A decoder can die mid-run for reasons the up-front verification cannot
       see: «Decoder failure», «Decoding task did not complete», «Codec
       reclaimed due to inactivity», a GPU process that went away. Prod 30 d:
       ~52 such events, and every one of them killed the WHOLE export — minutes
       of decoding thrown away over one window that a fresh decoder would have
       served. A fresh decoder per window is already the design here, so the
       retry costs one window, not the run.
       Ladder: same config again (transient), then the config with the
       hardwareAcceleration hint dropped (let the browser choose), then the
       same config on progressively smaller windows (see below). Bounded, and
       only ever after a real failure. */
    async function decodeWindow(g, lo, hi, want) {
        var cfgs = [decoderCfg];
        if (decoderCfg && decoderCfg.hardwareAcceleration) {
            var noHint = Object.assign({}, decoderCfg);
            delete noHint.hardwareAcceleration;
            cfgs.push(decoderCfg, noHint);
        } else {
            cfgs.push(decoderCfg);
        }
        /* Two different failures need two different answers, and treating them
           alike wastes the expensive one.
             · a DECODER failure (codec reclaimed, GPU process gone, «Decoder
               failure») is transient or config-specific → next config, same
               window. That is the original ladder, and it stays.
             · a FRAME LOSS is the device saying it could not hold what we asked
               for. Re-running the SAME window on the SAME device under the SAME
               pressure just fails again, so shrink instead of retrying blind.
           Measured on an injected GPU ceiling (97-frame GOP): the blind version
           spent THREE full-GOP decodes per size before shrinking; this one
           spends one. Bounded either way — at most MAX_WINDOW_ATTEMPTS. */
        var span = hi - lo + 1, lo0 = lo, hi0 = hi;
        var cfgIdx = 0, lastErr = null, entry;
        for (var attempt = 0; attempt < MAX_WINDOW_ATTEMPTS; attempt++) {
            var shrunk = (span < (hi0 - lo0 + 1)) ? span : 0;
            try {
                entry = await decodeWindowOnce(g, lo, hi, cfgs[Math.min(cfgIdx, cfgs.length - 1)]);
                /* A shrink that worked is this device telling us how much it can
                   hold. Remember it for the rest of the run, or every later
                   window pays the same failures over again. */
                if (shrunk && shrunk < windowFrames) {
                    windowFrames = shrunk;
                    try { window.__ae && window.__ae('feature_use', {
                        slug: 'reverse-video', f: 'rwc_window_shrunk', to: windowFrames,
                        why: (lastErr && lastErr.lostWhy || '').slice(0, 40)
                    }); } catch (_) {}
                }
                return entry;
            } catch (e) {
                if (signal && signal.aborted) throw e;
                if (e && e.name === 'AbortError') throw e;
                lastErr = e;
                try { window.__ae && window.__ae('feature_use', {
                    slug: 'reverse-video', f: 'rwc_window_retry', attempt: attempt,
                    lost: e && e.framesLost || 0, of: e && e.framesOf || 0,
                    first: (e && typeof e.firstLost === 'number') ? e.firstLost : -1,
                    span: hi - lo + 1, cfg: cfgIdx,
                    why: (e && e.lostWhy || '').slice(0, 40),
                    reason: String((e && e.message) || e).slice(0, 70)
                }); } catch (_) {}

                if (e && e.framesLost) {
                    if (span <= MIN_WINDOW_FRAMES) break;      // as small as it goes
                    span = Math.max(MIN_WINDOW_FRAMES, span >> 1);
                    var anchor = (want === undefined) ? hi0 : Math.min(hi0, Math.max(lo0, want));
                    hi = Math.min(hi0, anchor + ((span - 1) >> 1));
                    lo = Math.max(lo0, hi - span + 1);
                    span = hi - lo + 1;
                } else {
                    if (cfgIdx + 1 >= cfgs.length) break;      // configs exhausted
                    cfgIdx++;
                }
            }
        }
        throw lastErr || new Error('decode-window-failed');
    }

    /* Where a decode for GOP `g` has to START.
       The model here used to be "a GOP is self-contained: feed it from its own
       keyframe and you get all of its frames". That holds for a CLOSED GOP and
       is false for an OPEN one, whose leading B-frames reference the GOP BEFORE
       it. Fed from its own keyframe, the decoder does not error — it emits a
       picture, and the picture is wrong. Measured on an x264 `open-gop=1`
       fixture: source frames 94 and 95 came back as frame 96, so the reversed
       clip carried two silently wrong frames and every gate still said OK.
       The tell is structural and cheap: a GOP that does not own its own earliest
       presentation frame (presRank[keyframe] > pStart) is open, so walk back. A
       closed GOP returns `g` unchanged, which is every ordinary file — this
       costs the normal path nothing. */
    function decodeStartGop(g) {
        /* ONE group back is enough (2026-11-04). That group starts cold too, and its
           own leading pictures are not fed (coldLead below) — they are shown in the
           group before IT, outside this window — so its trailing pictures, the ones
           this group's leading pictures reference, decode exactly. Walking on while
           the start group was open decoded up to four more groups per window to throw
           away: an iPhone's HEVC is open in every group after the first. */
        if (g > 0 && dm.presRank[dm.gops[g].sStart] > dm.gops[g].pStart) g--;
        /* …and the group it lands on must be one a FRESH decoder can start at: an
           H.264 open GOP marks recovery-point I-frames as sync, and Chromium refuses
           them as a first chunk — only an IDR starts it (the 48-frame fixture of the
           frame contract: sync samples #10, #22, #34 are non-IDR). An HEVC CRA starts
           one. The owner of the answer: window.__leadingPictures.startsADecoder. */
        var L = window.__leadingPictures, guard = 0;
        if (L && typeof L.startsADecoder === 'function') {
            while (g > 0 && guard++ < 64 && !L.startsADecoder(dm.codec, dm.samples[dm.gops[g].sStart].data)) g--;
        }
        return g;
    }
    /* The leading pictures of the keyframe a window's decoder starts at cold (after
       it in decode order, shown before it — an iPhone's HEVC CRA and its RASL): they
       reference what this decoder never saw, and WebKit answers the first of them
       with «Decoder failure» and closes the decoder. They are shown before the start
       group, never in the window. One owner of the rule: window.__leadingPictures
       (timbrica-core.js). */
    function coldLead(sFrom, sEnd) {
        var set = {};
        if (typeof window.__leadingPictures !== 'function') return set;
        var L = window.__leadingPictures(sEnd, sFrom, function (i) { return dm.samples[i].cts; },
            function (i) { return dm.samples[i].type === 'key'; });
        for (var i = 0; i < L.length; i++) set[L[i]] = 1;
        return set;
    }

    async function decodeWindowOnce(g, lo, hi, useCfg) {
        var gop = dm.gops[g];
        lo = Math.max(gop.pStart, lo);
        hi = Math.min(gop.pEnd, hi);
        // cts → presentation index, and the last decode-order sample whose frame
        // is ≤ hi (references always precede in decode order, so feeding from the
        // start sample to here decodes every frame the window needs).
        var sFrom = dm.gops[decodeStartGop(g)].sStart;
        var lead = coldLead(sFrom, gop.sEnd);
        var ctsToPres = new Map();
        var feedEnd = sFrom;
        for (var i = sFrom; i < gop.sEnd; i++) {
            ctsToPres.set(dm.samples[i].cts, dm.presRank[i]);
            if (dm.presRank[i] <= hi && i > feedEnd) feedEnd = i;
        }
        var slot = new Array(hi - lo + 1);   // slot[presIdx - lo] = bitmap
        var pending = [];                    // in-flight createImageBitmap promises
        var inFlight = 0;                    // copies the GPU is still working on
        var lostWhy = '';                    // first reason a frame failed to convert
        var err = null;
        var decoder = new VideoDecoder({
            output: function (frame) {
                var p = ctsToPres.get(frame.timestamp);
                if (p !== undefined && p >= lo && p <= hi) {
                    var idx = p - lo;
                    /* A frame that decodes but fails to become an ImageBitmap is a
                       LOST FRAME, and it used to be swallowed here (`.catch(function(){})`)
                       — the run went on, the missing slot was papered over at serve
                       time with a neighbour, and the user got a file that plays and
                       is wrong. Bug report 2026-09-19 (#6865, Android/Chrome 139,
                       832x448/97f): the last 20 source frames were lost, so the
                       reversed clip opened with a 0.8 s freeze and resumed 20 frames
                       in — reproduced exactly by rejecting these conversions. Record
                       the reason; the window check below turns it into a retry, and
                       a retry that still comes up short fails the lane instead of
                       lying. */
                    inFlight++;
                    try {
                        pending.push(createImageBitmap(frame).then(function (bmp) {
                            slot[idx] = bmp; inFlight--;
                        }, function (e) {
                            inFlight--; lostWhy = lostWhy || String((e && e.message) || e || 'bitmap');
                        }));
                    } catch (e) {
                        inFlight--; lostWhy = lostWhy || String((e && e.message) || e || 'bitmap-throw');
                    }
                }
                try { frame.close(); } catch (_) {}
            },
            error: function (e) { err = e instanceof Error ? e : new Error(String(e && e.message || e)); }
        });
        try {
            decoder.configure(useCfg || decoderCfg);
            for (var j = sFrom; j <= feedEnd; j++) {
                if (signal && signal.aborted) throw new DOMException('Aborted', 'AbortError');
                if (lead[j]) continue;
                var s = dm.samples[j];
                decoder.decode(new EncodedVideoChunk({
                    type: s.type, timestamp: s.cts, duration: s.dur, data: s.data
                }));
                // Backpressure — keep the decode queue shallow so a long GOP
                // doesn't overrun the decoder's hw output-buffer pool (w3c/
                // webcodecs #680). Drain to ≤8 before feeding more.
                /* Two queues can overrun, not one. `decodeQueueSize` guards the
                   decoder's own input; `inFlight` guards the GPU copies started
                   from its output, which had no bound at all.
                   What it is worth, measured rather than assumed — the same
                   97-frame GOP with an artificial delay on each copy, which is
                   what a phone's GPU looks like next to this desktop:

                       copy latency   peak in-flight, no bound   with bound
                          ~0 (desktop)            1                  1
                          25 ms                  36                 31
                         150 ms                  94                 71

                   So on a fast machine it never fires, and on a slow one it cuts
                   peak concurrent copies by ~24 % at no cost in wall time (19.0 s
                   against 21.4 s, within noise). It does NOT clamp to the
                   constant: the decoder emits in bursts and the callback enqueues
                   several before the feed loop reaches this drain.
                   ⚠️ It is a pressure reducer, not the fix for #6865 — what that
                   report ran out of was GPU memory for the frames the window
                   HOLDS, and the answer to that is the shrink ladder above. */
                if (decoder.decodeQueueSize > 16 || inFlight > MAX_INFLIGHT_COPIES) {
                    await new Promise(function (res) {
                        (function chk() {
                            if (err || decoder.state === 'closed' || (signal && signal.aborted)
                                || (decoder.decodeQueueSize <= 8 && inFlight <= (MAX_INFLIGHT_COPIES >> 1))) res();
                            else setTimeout(chk, 4);
                        })();
                    });
                    if (err) throw err;
                }
            }
            await decoder.flush();
            if (err) throw err;
            await Promise.all(pending);
            /* THE CONTRACT. A window either holds every frame in [lo,hi] or it is
               not a window — it is a hole with a plausible picture in it. Counting
               the holes here is what turns a silent wrong file into a retry (the
               ladder above re-decodes on a fresh decoder) and, if the retry is
               still short, into an honest failure that drops the run to the FFmpeg
               lane. Slower, correct, and visible. */
            var missing = 0, firstMiss = -1;
            for (var m = 0; m < slot.length; m++) {
                if (!slot[m]) { missing++; if (firstMiss < 0) firstMiss = m + lo; }
            }
            if (missing) {
                var le = new Error('frames-lost:' + missing + '/' + slot.length
                    + (lostWhy ? ':' + lostWhy.slice(0, 40) : ''));
                le.framesLost = missing; le.framesOf = slot.length;
                le.firstLost = firstMiss; le.lostWhy = lostWhy;
                throw le;
            }
        } catch (windowErr) {
            /* A failed attempt still decoded frames into `slot`; with the retry
               ladder above that would be up to three orphaned windows of
               full-resolution ImageBitmaps on a phone. Settle the in-flight
               createImageBitmap promises, then close what arrived. */
            try { await Promise.all(pending); } catch (_) {}
            for (var c = 0; c < slot.length; c++) { try { if (slot[c]) slot[c].close(); } catch (_) {} }
            throw windowErr;
        } finally {
            try { if (decoder.state !== 'closed') decoder.close(); } catch (_) {}
        }

        var entry = { g: g, lo: lo, hi: hi, bitmaps: slot };
        cache.push(entry);
        while (cache.length > maxWindows) {
            var old = cache.shift();
            old.bitmaps.forEach(function (b) { try { if (b) b.close(); } catch (_) {} });
        }
        return entry;
    }

    return {
        maxGopFrames: dm.maxGopFrames,
        getBitmap: async function (presIdx) {
            if (presIdx < 0) presIdx = 0;
            if (presIdx >= dm.pres.length) presIdx = dm.pres.length - 1;
            var g = gopForPres(presIdx);
            /* No GOP covers this frame: the sample table and the presentation
               map disagree. Serving GOP 0 (what used to happen) means quietly
               drawing an unrelated picture. */
            if (g < 0) throw new Error('frames-lost:no-gop-for-' + presIdx);
            var gop = dm.gops[g];

            var e = null;
            for (var i = cache.length - 1; i >= 0; i--) {   // search MRU-first
                var c = cache[i];
                if (c.g === g && presIdx >= c.lo && presIdx <= c.hi) { e = c; break; }
            }
            if (e) {
                if (cache[cache.length - 1] !== e) { cache.splice(cache.indexOf(e), 1); cache.push(e); }   // touch → MRU
            } else {
                // Whole GOP fits → cache it all (fast path). Otherwise place the
                // window in this GOP's read direction: reverse slides DOWN →
                // trailing window ending at presIdx; forward slides UP → leading
                // window starting at presIdx. Direction is per-GOP so loop mode's
                // interleaved head/tail streams don't confuse each other.
                var lo, hi;
                if (gop.frames <= windowFrames) {
                    lo = gop.pStart; hi = gop.pEnd;
                } else {
                    var prev = gopLastPres[g];
                    var dir = (prev === undefined) ? -1 : (presIdx < prev ? -1 : 1);
                    if (dir < 0) { hi = presIdx; lo = presIdx - windowFrames + 1; }
                    else { lo = presIdx; hi = presIdx + windowFrames - 1; }
                }
                e = await decodeWindow(g, lo, hi, presIdx);
            }
            gopLastPres[g] = presIdx;

            var k = presIdx - e.lo;
            var bmp = (k >= 0 && k < e.bitmaps.length) ? e.bitmaps[k] : null;
            /* Here stood "rare decode gap — serve the nearest decoded neighbour".
               It is the line that made #6865 a wrong FILE instead of a fallback:
               twenty missing frames all resolved to the same surviving neighbour,
               so the reversed clip opened with a freeze and resumed 20 frames in,
               and nothing anywhere knew. A provider that cannot produce the frame
               it was asked for must say so — decodeWindowOnce has already retried
               on a fresh decoder by the time we get here. */
            if (!bmp) throw new Error('frames-lost:serve-' + presIdx);
            return bmp;
        },
        destroy: function () {
            cache.forEach(function (e) { e.bitmaps.forEach(function (b) { try { if (b) b.close(); } catch (_) {} }); });
            cache = [];
        }
    };
}

/* ───────────────────────── audio ─────────────────────────
   Decode the source audio to an AudioBuffer, apply trim, optional reverse,
   optional speed (playbackRate resample), and — for boomerang — concat the
   forward + processed halves. Returns an AudioBuffer or null. */
async function decodeSourceAudio(file, ctx) {
    // Prefer the robust WebCodecs decoder for mov/aac (native decodeAudioData
    // fails on many QuickTime/Yandex containers); fall back to decodeAudioData.
    if (window.WebCodecsAudioDecode && typeof window.WebCodecsAudioDecode.supported === 'function'
        && window.WebCodecsAudioDecode.supported(file)) {
        try { return await window.WebCodecsAudioDecode.decode(file, ctx); } catch (_) {}
    }
    var buf = await file.arrayBuffer();
    return await ctx.decodeAudioData(buf);
}

function sliceAudio(ctx, ab, startSec, endSec) {
    if (startSec <= 0 && (endSec == null || endSec >= ab.duration)) return ab;
    var sr = ab.sampleRate;
    var s = Math.max(0, Math.floor(startSec * sr));
    var e = Math.min(ab.length, Math.floor((endSec == null ? ab.duration : endSec) * sr));
    var n = Math.max(1, e - s);
    var out = ctx.createBuffer(ab.numberOfChannels, n, sr);
    for (var c = 0; c < ab.numberOfChannels; c++) {
        out.getChannelData(c).set(ab.getChannelData(c).subarray(s, e));
    }
    return out;
}

function reverseAudio(ctx, ab) {
    var out = ctx.createBuffer(ab.numberOfChannels, ab.length, ab.sampleRate);
    for (var c = 0; c < ab.numberOfChannels; c++) {
        var src = ab.getChannelData(c), dst = out.getChannelData(c), n = src.length;
        for (var i = 0; i < n; i++) dst[i] = src[n - 1 - i];
    }
    return out;
}

async function resampleSpeed(ab, speed) {
    if (Math.abs(speed - 1) < 0.001) return ab;
    var outLen = Math.max(1, Math.round(ab.length / speed));
    var oac = new OfflineAudioContext(ab.numberOfChannels, outLen, ab.sampleRate);
    var src = oac.createBufferSource();
    src.buffer = ab; src.playbackRate.value = speed;
    src.connect(oac.destination); src.start();
    return await oac.startRendering();
}

// Resample to 48000 Hz when the source rate isn't one the AAC/Opus encoder
// accepts (44100 / 48000). OfflineAudioContext at the target rate resamples on
// render. No-op for already-safe rates.
async function toEncoderSafeRate(ab) {
    if (ab.sampleRate === 44100 || ab.sampleRate === 48000) return ab;
    var outLen = Math.max(1, Math.round(ab.duration * 48000));
    var oac = new OfflineAudioContext(ab.numberOfChannels, outLen, 48000);
    var src = oac.createBufferSource();
    src.buffer = ab; src.connect(oac.destination); src.start();
    return await oac.startRendering();
}

function concatAudio(ctx, a, b) {
    var ch = Math.max(a.numberOfChannels, b.numberOfChannels);
    var out = ctx.createBuffer(ch, a.length + b.length, a.sampleRate);
    for (var c = 0; c < ch; c++) {
        var dst = out.getChannelData(c);
        dst.set(a.getChannelData(Math.min(c, a.numberOfChannels - 1)), 0);
        dst.set(b.getChannelData(Math.min(c, b.numberOfChannels - 1)), a.length);
    }
    return out;
}

// Seamless-loop audio: drop the last K samples and equal-power crossfade them
// onto the first K, so the audio loops without a click. Mirrors the video's
// dropped-K-frames crossfade → output length = n - K = same as the video.
function loopCrossfadeAudio(ctx, ab, crossSec) {
    var sr = ab.sampleRate, n = ab.length;
    var K = Math.max(1, Math.min(Math.floor(n / 2), Math.round((crossSec || 0.5) * sr)));
    var outLen = n - K;
    if (outLen < 1) return ab;
    var out = ctx.createBuffer(ab.numberOfChannels, outLen, sr);
    for (var c = 0; c < ab.numberOfChannels; c++) {
        var src = ab.getChannelData(c), dst = out.getChannelData(c);
        for (var o = K; o < outLen; o++) dst[o] = src[o];                       // body
        for (var i = 0; i < K; i++) {                                          // crossfade head+tail
            var a = (i + 0.5) / K;
            dst[i] = src[n - K + i] * Math.cos(a * Math.PI / 2) + src[i] * Math.sin(a * Math.PI / 2);
        }
    }
    return out;
}

async function buildOutputAudio(file, opts, signal, hasAudio) {
    if (opts.audio === 'mute' || opts.format === 'gif') return null;
    // No audio track → MUTE, don't throw 'audio-failed'. Previously a silent
    // video (screen-recording, muted social clip, image-slideshow export) made
    // decodeAudioData throw "Unable to decode audio data", which dropped the
    // whole fast WebCodecs path to slow FFmpeg-WASM for a video with no audio
    // to lose. Detecting the missing track keeps it on the HW path.
    if (hasAudio === false) return null;
    var AC = window.AudioContext || window.webkitAudioContext;
    if (!AC) return null;
    var ctx = new AC();
    try {
        var ab = await decodeSourceAudio(file, ctx);
        if (!ab || !ab.length) return null;
        if (signal && signal.aborted) throw new DOMException('Aborted', 'AbortError');
        // The AAC/Opus encoder only accepts 44100 / 48000 Hz. WebCodecsAudioDecode
        // returns the SOURCE rate (e.g. 22050/32000/16000), which the encoder then
        // rejects with "Unsupported sample rate". Resample to 48000 so those files
        // export with sound instead of falling back to FFmpeg.
        ab = await toEncoderSafeRate(ab);

        var out;
        if (opts.mode === 'segment' && opts.segment) {
            // head forward + span (reversed to match video, or kept) + tail forward.
            var head = sliceAudio(ctx, ab, 0, opts.segment.start);
            var midF = sliceAudio(ctx, ab, opts.segment.start, opts.segment.end);
            var mid = (opts.audio === 'reverse') ? reverseAudio(ctx, midF) : midF;
            var tail = sliceAudio(ctx, ab, opts.segment.end, ab.duration);
            out = concatAudio(ctx, concatAudio(ctx, head, mid), tail);
        } else {
            // Trim first, in source direction.
            var fwd = opts.trim ? sliceAudio(ctx, ab, opts.trim.start, opts.trim.end) : ab;
            if (opts.mode === 'loop') {
                // seamless forward loop: equal-power crossfade the tail into the head
                // (A/V aligned with the video's dropped-K-frames crossfade).
                out = loopCrossfadeAudio(ctx, fwd, +opts.crossfade || 0.5);
            } else if (opts.mode === 'boomerang') {
                // forward half plays forward; back half mirrors the reversed video.
                var backHalf = (opts.audio === 'reverse') ? reverseAudio(ctx, fwd) : fwd;
                var cycle = concatAudio(ctx, fwd, backHalf);
                out = cycle;
                var loops = Math.max(1, Math.min(5, parseInt(opts.loops, 10) || 1));
                for (var li = 1; li < loops; li++) out = concatAudio(ctx, out, cycle);
            } else {
                // plain reverse: 'reverse' → reversed audio; 'keep' → original direction.
                out = (opts.audio === 'reverse') ? reverseAudio(ctx, fwd) : fwd;
            }
        }
        out = await resampleSpeed(out, opts.speed || 1);
        return out;
    } catch (e) {
        // Audio decode/build failed — signal the caller to fall back so audio
        // is never silently dropped.
        throw new Error('audio-failed:' + ((e && e.message) || 'decode'));
    } finally {
        try { ctx.close(); } catch (_) {}
    }
}

/* ───────────────────────── support probe ───────────────────────── */
async function isSupported(o) {
    o = o || {};
    if (typeof VideoDecoder === 'undefined' || typeof VideoEncoder === 'undefined'
        || typeof VideoFrame === 'undefined' || typeof EncodedVideoChunk === 'undefined') {
        return { ok: false, reason: 'no-webcodecs' };
    }
    if (!window.CanvasVideoExport) return { ok: false, reason: 'no-canvas-export' };
    if (!o.file) return { ok: false, reason: 'no-file' };
    if (!isMp4Family(o.file)) return { ok: false, reason: 'not-mp4-family' };

    var dm;
    try { dm = await demux(o.file); }
    catch (e) { return { ok: false, reason: 'demux:' + ((e && e.message) || 'fail') }; }

    var dpick = await pickDecoderConfig(dm.codec, dm.description, firstKeyframe(dm),
                                        { w: dm.codedW, h: dm.codedH });
    if (!dpick) return { ok: false, reason: 'decoder-rejects-' + dm.codec };

    // Frame-cache budget → sliding-window size. If the largest GOP fits the
    // budget, the window IS the GOP (decoded once, no re-decode — the normal
    // path). If not, the provider windows the GOP at FULL resolution, decoding
    // ≤ windowsPerGop times. The pathological tail — a single enormous GOP, or
    // frames so large even a minimal window won't fit — goes to FFmpeg (which
    // streams at full quality) rather than a quality-losing downscale. Loop mode
    // holds 2 windows (head+tail), so it halves the window.
    var frameBytes = dm.codedW * dm.codedH * 4;
    var cacheMult = (o.mode === 'loop') ? 2 : 1;
    var budget = frameCacheBudget();
    var windowFrames = Math.max(1, Math.floor(budget / (frameBytes * cacheMult)));
    if (windowFrames < MIN_WINDOW_FRAMES) {
        return { ok: false, reason: 'frame-too-large:' + Math.round(frameBytes / 1048576) + 'mb' };
    }
    var windowsPerGop = Math.ceil(dm.maxGopFrames / windowFrames);
    if (windowsPerGop > MAX_REDECODE_WINDOWS) {
        return { ok: false, reason: 'gop-too-large:' + dm.maxGopFrames + '/' + windowFrames };
    }
    windowFrames = Math.min(windowFrames, dm.maxGopFrames);   // never allocate past one GOP

    var rot90 = dm.rotation === 90 || dm.rotation === 270;
    var outW = rot90 ? dm.codedH : dm.codedW;   // full resolution — no downscale
    var outH = rot90 ? dm.codedW : dm.codedH;

    // GIF output uses gif.js (loaded on demand in encodeGif), not the video
    // encoder — so it only needs a working decoder, not CanvasVideoExport.
    var encHW = false;
    if (o.format !== 'gif') {
        var enc = await window.CanvasVideoExport.isSupported({
            format: o.format === 'webm' ? 'webm' : 'mp4',
            width: outW, height: outH, fps: dm.fps,
            audioSampleRate: 48000, audioChannels: 2
        });
        // CanvasVideoExport probes the audio encoder unconditionally, but a muted
        // export never creates one. AAC encode is unsupported on Firefox / desktop
        // Linux, so treat a 'no-audio-encoder' rejection as OK when audio is muted
        // (the video encoder passed first, or the reason would be no-video-encoder).
        if (!enc.ok && !(o.audio === 'mute' && enc.reason === 'no-audio-encoder')) {
            return { ok: false, reason: 'encoder:' + enc.reason };
        }
        encHW = enc.hardware;
    }

    return {
        ok: true, reason: '',
        decoderHardware: dpick.hardware, encoderHardware: encHW,
        codec: dm.codec, width: outW, height: outH, fps: dm.fps,
        frames: dm.samples.length, maxGopFrames: dm.maxGopFrames,
        durationSec: dm.durationSec, rotation: dm.rotation,
        windowFrames: windowFrames, windowsPerGop: windowsPerGop,
        _dm: dm, _decoderCfg: dpick.config
    };
}

/* ───────────────────────── main reverse ───────────────────────── */
async function reverse(file, opts, hooks) {
    opts = opts || {}; hooks = hooks || {};
    var signal = hooks.signal;
    function stage(n) { if (hooks.onStage) try { hooks.onStage(n); } catch (_) {} }
    function throwIfAborted() { if (signal && signal.aborted) throw new DOMException('Aborted', 'AbortError'); }

    if (!isMp4Family(file)) throw new Error('not-mp4-family');

    stage('demux');
    // Reuse the probe (also validates support) so we don't demux twice.
    var sup = await isSupported({ file: file, format: opts.format, audio: opts.audio, mode: opts.mode });
    if (!sup.ok) throw new Error(sup.reason);
    throwIfAborted();

    var dm = sup._dm;
    var speed = opts.speed || 1;
    var rot90 = dm.rotation === 90 || dm.rotation === 270;
    // Full coded dims — the window provider serves frames at SOURCE resolution
    // (no downscale); drawOriented draws them into the (rotated) output canvas.
    var cw = dm.codedW, ch = dm.codedH;
    var outW = rot90 ? ch : cw;
    var outH = rot90 ? cw : ch;
    var fps = dm.fps;

    // Presentation-frame window after trim.
    var pStart = 0, pEnd = dm.pres.length;   // [pStart, pEnd) presentation indices
    if (opts.trim && (opts.trim.start > 0.05 || opts.trim.end < dm.durationSec - 0.05)) {
        var t0 = opts.trim.start * 1e6, t1 = opts.trim.end * 1e6;
        pStart = dm.pres.length; pEnd = 0;
        for (var k = 0; k < dm.pres.length; k++) {
            var cts = dm.samples[dm.pres[k]].cts;
            if (cts >= t0 && cts <= t1) { if (k < pStart) pStart = k; if (k + 1 > pEnd) pEnd = k + 1; }
        }
        if (pStart >= pEnd) { pStart = 0; pEnd = dm.pres.length; }
    }
    var N = pEnd - pStart;                    // source frames in range

    if (hooks.onInfo) try {
        hooks.onInfo({
            decoderHardware: sup.decoderHardware, encoderHardware: sup.encoderHardware,
            frames: N, maxGopFrames: dm.maxGopFrames, rotation: dm.rotation,
            width: outW, height: outH, fps: fps,
            // windowed: the largest GOP exceeded the frame-cache budget so the
            // provider re-decodes bounded windows at FULL resolution. Telemetry
            // only — there is NO user-facing quality change to surface.
            windowed: sup.windowFrames < dm.maxGopFrames, windowsPerGop: sup.windowsPerGop
        });
    } catch (_) {}

    // ── audio (may throw 'audio-failed:*' → caller falls back). Pass the
    //    demuxed hasAudio so a silent video mutes instead of dropping to FFmpeg. ──
    var outAudio = null;
    if (opts.audio !== 'mute') {
        stage('audio');
        outAudio = await buildOutputAudio(file, opts, signal, dm.hasAudio);
        throwIfAborted();
    }

    // ── output frame mapping (mode: reverse | boomerang | segment | loop) ──
    // Everything is computed in an "assembled" source-frame sequence (before
    // speed); the output samples it at j*speed. GIF samples the same sequence
    // at k*fps/gifFps. One mapping + drawAssembled serves all modes and both
    // encoders.
    var mode = (opts.mode === 'boomerang' || opts.mode === 'segment' || opts.mode === 'loop') ? opts.mode : 'reverse';
    var loops = Math.max(1, Math.min(5, parseInt(opts.loops, 10) || 1));

    // Segment mode: reverse ONLY [segStart,segEnd]; head + tail play forward.
    // (blade passes trim=null in segment mode, so pStart/pEnd span the whole clip.)
    var sp0 = pStart, sp1 = pEnd;   // seg presentation range (absolute pres idx)
    if (mode === 'segment' && opts.segment) {
        var s0 = opts.segment.start * 1e6, s1 = opts.segment.end * 1e6;
        sp0 = pEnd; sp1 = pStart;
        for (var si = pStart; si < pEnd; si++) {
            var scts = dm.samples[dm.pres[si]].cts;
            if (scts >= s0 && scts <= s1) { if (si < sp0) sp0 = si; if (si + 1 > sp1) sp1 = si + 1; }
        }
        if (sp0 >= sp1 || (sp1 - sp0) < 2) { mode = 'reverse'; }   // empty span → plain reverse
    }
    var segHead = sp0 - pStart, segMid = sp1 - sp0;

    // Loop mode: seamless crossfade — drop the last K frames and cross-dissolve
    // them onto the first K, so the last output frame is the neighbour of the
    // first and the clip loops with no jump. Blend is done in drawAssembled;
    // the provider keeps 2 GOPs resident so head+tail don't thrash the cache.
    var loopK = 0;
    if (mode === 'loop') {
        var crossSec = Math.max(0.1, Math.min(2, +opts.crossfade || 0.5));
        loopK = Math.max(1, Math.min(Math.floor(N / 2), Math.round(crossSec * fps)));
    }

    function assembledPresIdx(a) {
        if (mode === 'boomerang') {
            var cyc = 2 * N, c = a % cyc;
            return (c < N) ? (pStart + c) : (pStart + (cyc - 1 - c));   // fwd then mirror
        }
        if (mode === 'segment') {
            if (a < segHead) return pStart + a;                                 // head forward
            if (a < segHead + segMid) return (sp1 - 1) - (a - segHead);         // span reversed
            return sp1 + (a - segHead - segMid);                                // tail forward
        }
        if (mode === 'loop') return pStart + a;                                 // forward body (crossfade in drawAssembled)
        return pStart + (N - 1 - a);                                           // reverse
    }
    var assembledLen = (mode === 'boomerang') ? (2 * N * loops)
                     : (mode === 'loop') ? (N - loopK) : N;

    // ── frame provider (loop needs 2 windows resident for head+tail crossfade;
    //    a huge GOP slides a bounded window at full resolution) ──
    var provider = makeFrameProvider(dm, sup._decoderCfg, signal, mode === 'loop' ? 2 : 1, sup.windowFrames);

    // Mode-aware draw of assembled index `a` (blends the loop crossfade region).
    // Shared by the video and GIF encoders so both get the seamless join.
    async function drawAssembled(ctx, a) {
        if (a < 0) a = 0; if (a > assembledLen - 1) a = assembledLen - 1;
        if (mode === 'loop' && a < loopK) {
            var tb = await provider.getBitmap(pStart + (N - loopK + a));         // tail (base)
            drawOriented(ctx, tb, dm.rotation, outW, outH, cw, ch);
            ctx.save(); ctx.globalAlpha = (a + 0.5) / loopK;                     // head fades in over the tail
            var hb = await provider.getBitmap(pStart + a);
            drawOriented(ctx, hb, dm.rotation, outW, outH, cw, ch);
            ctx.restore();
        } else {
            var bmp = await provider.getBitmap(assembledPresIdx(a));
            drawOriented(ctx, bmp, dm.rotation, outW, outH, cw, ch);
        }
    }

    var totalOut = Math.max(1, Math.round(assembledLen / speed));

    stage('encode');
    var blob;
    if (opts.format === 'gif') {
        blob = await encodeGif(drawAssembled, {
            fps: fps, gifFps: opts.gifFps || 15, gifWidth: opts.gifWidth || 480,
            outW: outW, outH: outH, speed: speed, totalOut: totalOut,
            signal: signal, onProgress: hooks.onProgress
        });
    } else {
        blob = await window.CanvasVideoExport.encode({
            width: outW, height: outH, fps: fps,
            durationSec: totalOut / fps,
            format: opts.format === 'webm' ? 'webm' : 'mp4',
            qualityCrf: Math.max(18, Math.min(28, parseInt(opts.crf, 10) || 23)),
            audioBuffer: outAudio,
            signal: signal,
            onProgress: hooks.onProgress,
            drawFrame: async function (ctx, j) {
                await drawAssembled(ctx, Math.round(j * speed));
            }
        });
    }

    provider.destroy();
    return blob;
}

/* Draw a coded-orientation bitmap into the (possibly rotated) output canvas. */
function drawOriented(ctx, bmp, rotation, outW, outH, cw, ch) {
    if (!rotation) { ctx.drawImage(bmp, 0, 0, cw, ch); return; }
    ctx.save();
    if (rotation === 90) { ctx.translate(outW, 0); ctx.rotate(Math.PI / 2); }
    else if (rotation === 180) { ctx.translate(outW, outH); ctx.rotate(Math.PI); }
    else if (rotation === 270) { ctx.translate(0, outH); ctx.rotate(-Math.PI / 2); }
    ctx.drawImage(bmp, 0, 0, cw, ch);
    ctx.restore();
}

/* ───────────────────────── GIF path (gif.js) ─────────────────────────
   WebCodecs can't emit GIF, so the reversed frames (HW-decoded) are palette-
   quantized by gif.js — better quality + faster than the old FFmpeg round-trip
   (which stays as the blade's fallback if this throws). */
var _gifLoad = null;
function ensureGifJs() {
    if (window.GIF) return Promise.resolve();
    if (_gifLoad) return _gifLoad;
    _gifLoad = new Promise(function (resolve, reject) {
        var s = document.createElement('script');
        s.src = '/vendor/gif.js/gif.js';
        s.onload = resolve;
        s.onerror = function () { _gifLoad = null; reject(new Error('gifjs-load-failed')); };
        document.head.appendChild(s);
    });
    return _gifLoad;
}

// drawFull(fctx, a) draws the (oriented + loop-blended) frame at assembled
// index `a` into the full-size canvas — the shared drawAssembled from reverse().
async function encodeGif(drawFull, o) {
    await ensureGifJs();
    if (typeof window.GIF !== 'function') throw new Error('gifjs-unavailable');
    var fps = o.fps, gifFps = Math.max(5, Math.min(30, o.gifFps || 15)), speed = o.speed || 1;
    var baseW = o.outW, baseH = o.outH;
    var scale = (o.gifWidth > 0) ? Math.min(1, o.gifWidth / baseW) : 1;
    var gw = Math.max(2, Math.round(baseW * scale)), gh = Math.max(2, Math.round(baseH * scale));

    // Draw at full display size (drawFull bakes rotation + loop crossfade), then
    // scale to the GIF size.
    var full = (typeof OffscreenCanvas !== 'undefined') ? new OffscreenCanvas(baseW, baseH) : document.createElement('canvas');
    full.width = baseW; full.height = baseH;
    var fctx = full.getContext('2d', { alpha: false });
    var canvas = (typeof OffscreenCanvas !== 'undefined') ? new OffscreenCanvas(gw, gh) : document.createElement('canvas');
    canvas.width = gw; canvas.height = gh;
    var ctx = canvas.getContext('2d', { alpha: false });

    var gif = new window.GIF({
        workers: 2, quality: 10, width: gw, height: gh,
        workerScript: '/vendor/gif.js/gif.worker.js', dither: false
    });
    var gifTotal = Math.max(1, Math.round((o.totalOut / fps) * gifFps));
    var delay = Math.round(1000 / gifFps);
    for (var k = 0; k < gifTotal; k++) {
        if (o.signal && o.signal.aborted) throw new DOMException('Aborted', 'AbortError');
        var a = Math.round((k * fps / gifFps) * speed);   // assembled index for this GIF frame
        await drawFull(fctx, a);
        ctx.drawImage(full, 0, 0, gw, gh);
        // Pass ImageData (a fresh copy) — gif.js rejects an OffscreenCanvas 2D
        // context because it isn't `instanceof CanvasRenderingContext2D`.
        gif.addFrame(ctx.getImageData(0, 0, gw, gh), { delay: delay });
        if (o.onProgress) o.onProgress(Math.min(0.9, (k / gifTotal) * 0.9));
    }
    return await new Promise(function (resolve, reject) {
        gif.on('finished', function (blob) { if (o.onProgress) o.onProgress(1); resolve(blob); });
        gif.on('abort', function () { reject(new Error('gif-aborted')); });
        try { gif.render(); } catch (e) { reject(e); }
    });
}

window.ReverseWebCodecs = { isSupported: isSupported, reverse: reverse, isMp4Family: isMp4Family };

})();
